import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchSettings();
    } else if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showMessage('error', language === 'tr' ? 'Ayarlar yüklenemedi' : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, updates, { withCredentials: true });
      setSettings(response.data.settings);
      showMessage('success', language === 'tr' ? 'Ayarlar başarıyla güncellendi' : 'Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      showMessage('error', language === 'tr' ? 'Ayarlar güncellenemedi' : 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(language === 'tr' ? 'Tüm ayarları varsayılana döndürmek istediğinize emin misiniz?' : 'Are you sure you want to reset all settings to defaults?')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/reset`, {}, { withCredentials: true });
      setSettings(response.data.settings);
      showMessage('success', language === 'tr' ? 'Ayarlar varsayılana döndürüldü' : 'Settings reset to defaults');
    } catch (error) {
      console.error('Error resetting settings:', error);
      showMessage('error', language === 'tr' ? 'Ayarlar sıfırlanamadı' : 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const tabs = [
    { id: 'general', icon: '🏠', label: language === 'tr' ? 'Genel' : 'General' },
    { id: 'features', icon: '⚡', label: language === 'tr' ? 'Özellikler' : 'Features' },
    { id: 'ai', icon: '🤖', label: language === 'tr' ? 'AI Ayarları' : 'AI Settings' },
    { id: 'limits', icon: '📊', label: language === 'tr' ? 'Limitler' : 'Limits' },
    { id: 'questions', icon: '📝', label: language === 'tr' ? 'Sorular' : 'Questions' },
    { id: 'maintenance', icon: '🔧', label: language === 'tr' ? 'Bakım' : 'Maintenance' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Sistem Ayarları' : 'System Settings'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                ⚙️ {language === 'tr' ? 'Sistem Ayarları' : 'System Settings'}
              </h1>
              <p className="text-indigo-100 text-lg">
                {language === 'tr' 
                  ? 'Platform yapılandırması ve kontrol paneli'
                  : 'Platform configuration and control panel'}
              </p>
            </div>
            <button
              onClick={handleReset}
              disabled={saving}
              className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-all disabled:opacity-50"
            >
              🔄 {language === 'tr' ? 'Varsayılana Dön' : 'Reset to Defaults'}
            </button>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className="flex items-center">
              <span className="text-2xl mr-3">{message.type === 'success' ? '✅' : '❌'}</span>
              <span className="font-semibold">{message.text}</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-fit px-6 py-4 font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${tab.id === tabs[0].id ? 'rounded-tl-xl' : ''} ${
                  tab.id === tabs[tabs.length - 1].id ? 'rounded-tr-xl' : ''
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Settings Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '🏠 Genel Ayarlar' : '🏠 General Settings'}
              </h2>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Site Başlığı' : 'Site Title'}
                </label>
                <input
                  type="text"
                  value={settings?.site_title || ''}
                  onChange={(e) => handleInputChange('site_title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Site Açıklaması' : 'Site Description'}
                </label>
                <textarea
                  value={settings?.site_description || ''}
                  onChange={(e) => handleInputChange('site_description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'İletişim E-postası' : 'Contact Email'}
                  </label>
                  <input
                    type="email"
                    value={settings?.contact_email || ''}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Destek Telefonu' : 'Support Phone'}
                  </label>
                  <input
                    type="tel"
                    value={settings?.support_phone || ''}
                    onChange={(e) => handleInputChange('support_phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={() => handleUpdate({
                  site_title: settings?.site_title,
                  site_description: settings?.site_description,
                  contact_email: settings?.contact_email,
                  support_phone: settings?.support_phone
                })}
                disabled={saving}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? '💾 Kaydet' : '💾 Save Changes')}
              </button>
            </div>
          )}

          {/* Feature Flags */}
          {activeTab === 'features' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '⚡ Özellik Ayarları' : '⚡ Feature Settings'}
              </h2>

              {[
                { key: 'enable_ai_chat', label: language === 'tr' ? 'AI Sohbet' : 'AI Chat', icon: '💬' },
                { key: 'enable_question_generator', label: language === 'tr' ? 'Soru Üretici' : 'Question Generator', icon: '📝' },
                { key: 'enable_mentor_applications', label: language === 'tr' ? 'Mentor Başvuruları' : 'Mentor Applications', icon: '👨‍🏫' },
                { key: 'enable_feedback_system', label: language === 'tr' ? 'Geri Bildirim Sistemi' : 'Feedback System', icon: '📢' },
                { key: 'enable_personalized_learning', label: language === 'tr' ? 'Kişiselleştirilmiş Öğrenme' : 'Personalized Learning', icon: '🎯' },
                { key: 'enable_booklet_designer', label: language === 'tr' ? 'Fasikül Tasarımcısı' : 'Booklet Designer', icon: '📚' }
              ].map((feature) => (
                <div key={feature.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center">
                    <span className="text-3xl mr-4">{feature.icon}</span>
                    <span className="font-semibold text-gray-800">{feature.label}</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.[feature.key] || false}
                      onChange={(e) => {
                        handleInputChange(feature.key, e.target.checked);
                        handleUpdate({ [feature.key]: e.target.checked });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* AI Settings */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '🤖 AI Ayarları' : '🤖 AI Settings'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'AI Sağlayıcı' : 'AI Provider'}
                  </label>
                  <select
                    value={settings?.ai_model_provider || 'openai'}
                    onChange={(e) => handleInputChange('ai_model_provider', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="google">Google</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Model Adı' : 'Model Name'}
                  </label>
                  <input
                    type="text"
                    value={settings?.ai_model_name || ''}
                    onChange={(e) => handleInputChange('ai_model_name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Maksimum Token' : 'Max Tokens'}
                  </label>
                  <input
                    type="number"
                    value={settings?.max_ai_tokens || 2000}
                    onChange={(e) => handleInputChange('max_ai_tokens', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Sıcaklık (Temperature)' : 'Temperature'}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={settings?.ai_temperature || 0.7}
                    onChange={(e) => handleInputChange('ai_temperature', parseFloat(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={() => handleUpdate({
                  ai_model_provider: settings?.ai_model_provider,
                  ai_model_name: settings?.ai_model_name,
                  max_ai_tokens: settings?.max_ai_tokens,
                  ai_temperature: settings?.ai_temperature
                })}
                disabled={saving}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? '💾 Kaydet' : '💾 Save Changes')}
              </button>
            </div>
          )}

          {/* User Limits */}
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '📊 Kullanıcı Limitleri' : '📊 User Limits'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Günlük AI Sohbet Limiti' : 'Daily AI Chat Limit'}
                  </label>
                  <input
                    type="number"
                    value={settings?.daily_ai_chat_limit || 50}
                    onChange={(e) => handleInputChange('daily_ai_chat_limit', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Günlük Soru Üretim Limiti' : 'Daily Question Gen Limit'}
                  </label>
                  <input
                    type="number"
                    value={settings?.daily_question_gen_limit || 20}
                    onChange={(e) => handleInputChange('daily_question_gen_limit', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Haftalık Fasikül Limiti' : 'Weekly Booklet Limit'}
                  </label>
                  <input
                    type="number"
                    value={settings?.max_booklets_per_week || 5}
                    onChange={(e) => handleInputChange('max_booklets_per_week', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={() => handleUpdate({
                  daily_ai_chat_limit: settings?.daily_ai_chat_limit,
                  daily_question_gen_limit: settings?.daily_question_gen_limit,
                  max_booklets_per_week: settings?.max_booklets_per_week
                })}
                disabled={saving}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? '💾 Kaydet' : '💾 Save Changes')}
              </button>
            </div>
          )}

          {/* Question Settings */}
          {activeTab === 'questions' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '📝 Soru Ayarları' : '📝 Question Settings'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Onay İçin Min. Soru Sayısı' : 'Min Questions for Approval'}
                  </label>
                  <input
                    type="number"
                    value={settings?.min_questions_for_approval || 3}
                    onChange={(e) => handleInputChange('min_questions_for_approval', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Fasikül Başına Max. Soru' : 'Max Questions per Booklet'}
                  </label>
                  <input
                    type="number"
                    value={settings?.max_questions_per_booklet || 50}
                    onChange={(e) => handleInputChange('max_questions_per_booklet', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span className="font-semibold text-gray-800">
                  {language === 'tr' ? 'Admin Sorularını Otomatik Onayla' : 'Auto-approve Admin Questions'}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings?.auto_approve_admin_questions || false}
                    onChange={(e) => {
                      handleInputChange('auto_approve_admin_questions', e.target.checked);
                      handleUpdate({ auto_approve_admin_questions: e.target.checked });
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <button
                onClick={() => handleUpdate({
                  min_questions_for_approval: settings?.min_questions_for_approval,
                  max_questions_per_booklet: settings?.max_questions_per_booklet
                })}
                disabled={saving}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? '💾 Kaydet' : '💾 Save Changes')}
              </button>
            </div>
          )}

          {/* Maintenance */}
          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? '🔧 Bakım Modu' : '🔧 Maintenance Mode'}
              </h2>

              <div className="flex items-center justify-between p-6 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
                <div>
                  <h3 className="font-bold text-gray-800 text-lg mb-1">
                    {language === 'tr' ? 'Bakım Modu' : 'Maintenance Mode'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'tr' 
                      ? 'Aktif olduğunda sadece adminler sisteme erişebilir'
                      : 'When active, only admins can access the system'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings?.maintenance_mode || false}
                    onChange={(e) => {
                      handleInputChange('maintenance_mode', e.target.checked);
                      handleUpdate({ maintenance_mode: e.target.checked });
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-16 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Bakım Mesajı' : 'Maintenance Message'}
                </label>
                <textarea
                  value={settings?.maintenance_message || ''}
                  onChange={(e) => handleInputChange('maintenance_message', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={language === 'tr' ? 'Kullanıcılara gösterilecek mesaj...' : 'Message to show users...'}
                />
              </div>

              <button
                onClick={() => handleUpdate({
                  maintenance_message: settings?.maintenance_message
                })}
                disabled={saving}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? '💾 Kaydet' : '💾 Save Changes')}
              </button>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
          <div className="flex items-start">
            <span className="text-3xl mr-4">ℹ️</span>
            <div>
              <h3 className="font-bold text-blue-900 mb-2">
                {language === 'tr' ? 'Önemli Notlar' : 'Important Notes'}
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {language === 'tr' ? 'Tüm değişiklikler anında aktif olur' : 'All changes take effect immediately'}</li>
                <li>• {language === 'tr' ? 'Kritik ayarları değiştirmeden önce yedek alın' : 'Backup before changing critical settings'}</li>
                <li>• {language === 'tr' ? 'Son güncelleme: ' : 'Last updated: '}{settings?.last_updated ? new Date(settings.last_updated).toLocaleString() : '-'}</li>
                <li>• {language === 'tr' ? 'Güncelleyen: ' : 'Updated by: '}{settings?.updated_by || '-'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
